import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconBack, IconEdit, IconTrash } from '../../components/icons';
import { useAuth } from '../../auth/useAuth';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';
import { roleLabel } from '../../utils/roles';

// TS port of the source (Falah) app's pages/system/UserDetail.jsx.
//
// ASSOCIATIONS is trimmed to the FK columns VNPF's User model actually has
// (member_id/supplier_id/customer_id — see UserController::RESPONSE_FIELDS
// on the backend); Falah's donor_id/student_id/vendor_id/agent_id/
// volunteer_id columns don't exist here (those modules were removed).
//
// Route destinations for Member/Supplier/Customer detail pages are a
// best-effort guess (Membership/Procurement/Revenue pages don't exist yet —
// they're Pass B/C's job) and the label fetch is deliberately tolerant of a
// 403/404 (the viewing admin may hold users.manage without also holding
// memberships.view/procurement.view/revenue.customers.view) — see this
// pass's report for the exact assumption Pass B/C should double-check.
interface UserDetailRecord {
  id: number;
  full_name: string;
  email: string;
  formatted_id: string;
  status: 'active' | 'inactive';
  mobile: string | null;
  address: string | null;
  gender: 'male' | 'female' | null;
  date_of_birth: string | null;
  photo_url: string | null;
  roles: string[];
  member_id: number | null;
  supplier_id: number | null;
  customer_id: number | null;
}

interface Association {
  key: string;
  label: string;
  to: string;
  name: string;
}

const ASSOCIATIONS: {
  key: 'member_id' | 'supplier_id' | 'customer_id';
  label: string;
  endpoint: string;
  to: (id: number) => string;
  name: (record: Record<string, unknown>) => string;
}[] = [
  {
    key: 'member_id',
    label: 'Member',
    endpoint: 'members',
    to: (id) => `/memberships/${id}`,
    name: (r) => (r.full_name as string | undefined) ?? `Member #${r.id}`,
  },
  {
    key: 'supplier_id',
    label: 'Supplier',
    endpoint: 'suppliers',
    to: (id) => `/procurement/suppliers/${id}`,
    name: (r) => (r.business_name as string | undefined) ?? `Supplier #${r.id}`,
  },
  {
    key: 'customer_id',
    label: 'Customer',
    endpoint: 'customers',
    to: (id) => `/revenue/customers/${id}`,
    name: (r) => (r.name as string | undefined) ?? `Customer #${r.id}`,
  },
];

export function UserDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [user, setUser] = useState<UserDetailRecord | null>(null);
  const [associations, setAssociations] = useState<Association[] | null>(null);

  useEffect(() => {
    api.get<UserDetailRecord>(`/users/${id}`).then((res) => setUser(res.data));
  }, [id]);

  useEffect(() => {
    if (!user) return;
    const links = ASSOCIATIONS.filter((a) => user[a.key]);
    if (links.length === 0) {
      setAssociations([]);
      return;
    }
    Promise.all(
      links.map((a) =>
        api
          .get<Record<string, unknown>>(`/${a.endpoint}/${user[a.key]}`)
          .then(
            (res): Association => ({
              key: a.key,
              label: a.label,
              to: a.to(user[a.key] as number),
              name: a.name(res.data),
            })
          )
          .catch((): null => null)
      )
    ).then((results) => setAssociations(results.filter((r): r is Association => r !== null)));
  }, [user]);

  const remove = async () => {
    if (!user) return;
    if (!(await confirmDelete(`Delete account for ${user.full_name}?`))) return;
    try {
      await api.delete(`/users/${id}`);
      navigate('/users');
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  if (!user) return null;

  return (
    <>
      <header className="page-header">
        <div className="page-header-actions">
          <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/users')} />
          <IconButton icon={IconEdit} label="Edit" to={`/users/${id}/edit`} />
          <IconButton
            icon={IconTrash}
            label="Delete"
            variant="danger-ghost"
            onClick={remove}
            hidden={user.id === currentUser?.id}
          />
        </div>
      </header>

      <div className="card">
        <div className="detail-summary">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {user.photo_url ? (
              <img
                src={user.photo_url}
                alt=""
                style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
              />
            ) : (
              <div className="avatar" style={{ width: 56, height: 56, fontSize: '1.25rem' }}>
                {user.full_name?.charAt(0) ?? '?'}
              </div>
            )}
            <div>
              <div className="detail-summary-title">{user.full_name}</div>
              <div className="detail-summary-service">
                {user.roles.map(roleLabel).join(', ') || 'No roles assigned'}
              </div>
            </div>
          </div>
          <div>
            <span className={`badge badge-${user.status === 'active' ? 'active' : 'inactive'}`}>
              {user.status === 'active' ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>

        <div className="detail-grid">
          <div className="detail-item">
            <span className="detail-item-label">User ID</span>
            <span className="detail-item-value">{user.formatted_id}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Email</span>
            <span className="detail-item-value">{user.email}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Mobile</span>
            <span className={`detail-item-value${user.mobile ? '' : ' empty'}`}>{user.mobile || 'Not set'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Address</span>
            <span className={`detail-item-value${user.address ? '' : ' empty'}`}>{user.address || 'Not set'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Gender</span>
            <span className={`detail-item-value${user.gender ? '' : ' empty'}`}>
              {user.gender ? user.gender.charAt(0).toUpperCase() + user.gender.slice(1) : 'Not set'}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Date of birth</span>
            <span className={`detail-item-value${user.date_of_birth ? '' : ' empty'}`}>
              {user.date_of_birth || 'Not set'}
            </span>
          </div>
        </div>
      </div>

      <div className="card">
        <h2>Roles</h2>
        <p className="muted">Every role assigned to this account.</p>
        {user.roles.length > 0 ? (
          <div className="badge-list">
            {user.roles.map((r) => (
              <span key={r} className="badge badge-neutral">
                {roleLabel(r)}
              </span>
            ))}
          </div>
        ) : (
          <p className="muted">No roles assigned.</p>
        )}
      </div>

      <div className="card">
        <h2>Associations</h2>
        <p className="muted">Records this account is linked to.</p>
        {associations === null ? (
          <p className="muted">Loading…</p>
        ) : associations.length > 0 ? (
          <div className="detail-grid">
            {associations.map((a) => (
              <div className="detail-item" key={a.key}>
                <span className="detail-item-label">{a.label}</span>
                <Link className="detail-item-value" to={a.to}>
                  {a.name}
                </Link>
              </div>
            ))}
          </div>
        ) : (
          <p className="muted">No linked records — this is a staff-only account.</p>
        )}
      </div>
    </>
  );
}
